import React from "react";
import { Link } from "react-router-dom";

const ROWS = [
  {
    name: "session_token",
    party: "Divergent Cinema CIC",
    type: "Strictly necessary",
    purpose: "Keeps you signed in after Google sign-in. HttpOnly, Secure, SameSite=None.",
    expires: "7 days, or until you sign out",
  },
  {
    name: "dc-cookie-notice-v1",
    party: "Divergent Cinema CIC (local storage)",
    type: "Strictly necessary (preference)",
    purpose: "Remembers that you have dismissed the cookie notice banner so it does not reappear on every visit.",
    expires: "Until cleared by you (browser local storage, not a traditional cookie)",
  },
  {
    name: "VISITOR_INFO1_LIVE, YSC, __Secure-YEC, PREF (and others)",
    party: "YouTube / Google",
    type: "Third-party · only loaded if you press play on an embedded video",
    purpose: "YouTube uses these to measure bandwidth, count views, and personalise content on their player. We use YouTube&apos;s privacy-enhanced (`youtube-nocookie.com`) mode so nothing is loaded until you actively start a video.",
    expires: "Up to 2 years (set by YouTube)",
  },
];

export default function Cookies() {
  return (
    <div className="bg-[#FFF8E7]" data-testid="page-cookies">
      <section className="relative overflow-hidden border-b-2 border-[#0A0A0A]">
        <div className="absolute inset-0 halftone-pink opacity-[0.07]" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="inline-block bg-[#FF2E63] text-[#FFF8E7] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-4 border-2 border-[#0A0A0A]">Legal</div>
          <h1 className="font-heading font-black text-[#0A0A0A] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            Cookie <span className="text-[#1A5DFF] [text-shadow:3px_3px_0_#0A0A0A]">policy.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#0A0A0A] leading-relaxed">
            Plain English. No tracking, no advertising. We use one strictly-necessary cookie, plus a small browser flag for the cookie notice. YouTube embeds may set their own cookies — only if you press play.
          </p>
          <p className="mt-3 text-sm text-[#2C2C2C]">Last updated: 23 May 2026</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <h2 className="font-heading font-black text-[#0A0A0A] text-2xl md:text-3xl tracking-tight mb-6">Cookies & similar technologies we use</h2>

        {/* Mobile: stacked cards */}
        <div className="space-y-4 md:hidden" data-testid="cookies-mobile">
          {ROWS.map((r) => (
            <div key={r.name} className="bg-white border-2 border-[#0A0A0A] pop-shadow p-5">
              <div className="text-xs uppercase tracking-[0.16em] font-bold text-[#FF2E63] mb-1">{r.type}</div>
              <div className="font-heading font-bold text-[#0A0A0A] text-lg break-all">{r.name}</div>
              <div className="text-xs text-[#2C2C2C] mt-1 mb-3">Set by {r.party}</div>
              <div className="text-sm text-[#0A0A0A] leading-relaxed">{r.purpose}</div>
              <div className="text-xs text-[#2C2C2C] mt-3"><span className="font-bold uppercase tracking-[0.16em]">Expires · </span>{r.expires}</div>
            </div>
          ))}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block overflow-x-auto" data-testid="cookies-table">
          <table className="w-full border-2 border-[#0A0A0A] bg-white">
            <thead className="bg-[#0A0A0A] text-[#FFF8E7]">
              <tr>
                <Th>Cookie / item</Th>
                <Th>Set by</Th>
                <Th>Type</Th>
                <Th>Purpose</Th>
                <Th>Expires</Th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, i) => (
                <tr key={r.name} className={`${i % 2 === 0 ? "bg-white" : "bg-[#FFE8A8]"} border-t-2 border-[#0A0A0A]`}>
                  <Td><code className="text-[13px] break-all">{r.name}</code></Td>
                  <Td>{r.party}</Td>
                  <Td>
                    <span className="inline-block text-xs uppercase tracking-[0.14em] font-bold px-2 py-1 bg-[#FFD93D] border-2 border-[#0A0A0A]">{r.type}</span>
                  </Td>
                  <Td>{r.purpose}</Td>
                  <Td>{r.expires}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="font-heading font-black text-[#0A0A0A] text-2xl md:text-3xl tracking-tight mt-12 mb-3">YouTube privacy-enhanced mode</h2>
        <p className="text-[#0A0A0A] leading-relaxed mb-3">
          Our Films page embeds videos via <code>youtube-nocookie.com</code>. This means YouTube does <strong>not</strong> set its tracking cookies the moment the page loads — only when you choose to press play. If you never play a video, no YouTube cookies are set.
        </p>

        <h2 className="font-heading font-black text-[#0A0A0A] text-2xl md:text-3xl tracking-tight mt-10 mb-3">Managing cookies</h2>
        <p className="text-[#0A0A0A] leading-relaxed mb-3">
          You can clear cookies and local storage for this site at any time via your browser&apos;s settings. Doing so will sign you out and re-show the cookie notice on your next visit.
        </p>
        <ul className="list-none pl-0 space-y-2 text-[#0A0A0A]">
          <li className="flex gap-3"><span className="text-[#FF2E63] font-black mt-1">·</span><span><strong>Chrome:</strong> Settings → Privacy & Security → Cookies and other site data</span></li>
          <li className="flex gap-3"><span className="text-[#FF2E63] font-black mt-1">·</span><span><strong>Safari:</strong> Settings → Privacy → Manage Website Data</span></li>
          <li className="flex gap-3"><span className="text-[#FF2E63] font-black mt-1">·</span><span><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</span></li>
        </ul>

        <h2 className="font-heading font-black text-[#0A0A0A] text-2xl md:text-3xl tracking-tight mt-10 mb-3">Questions</h2>
        <p className="text-[#0A0A0A] leading-relaxed">
          Email <a className="underline" href="mailto:create@divergentcinema.co.uk">create@divergentcinema.co.uk</a> or see our <Link to="/privacy" className="underline">privacy policy</Link>.
        </p>

        <div className="border-t-2 border-[#0A0A0A] mt-10 pt-8 flex flex-wrap gap-3">
          <Link to="/privacy" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] rounded-none px-5 py-3 text-sm font-medium" data-testid="cookies-privacy-link">Privacy policy</Link>
          <Link to="/contact" className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-5 py-3 text-sm font-medium">Contact us</Link>
        </div>
      </section>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left text-xs uppercase tracking-[0.16em] font-bold px-4 py-3 align-top">{children}</th>;
}

function Td({ children }) {
  return <td className="text-sm text-[#0A0A0A] px-4 py-3 align-top">{children}</td>;
}
